import type { ModuleAtelierPayload } from './types';

const record = (value: unknown): value is Record<string, unknown> => Boolean(value)
  && typeof value === 'object' && !Array.isArray(value);
const exactKeys = (value: Record<string, unknown>, keys: readonly string[]) => {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
};
const nonempty = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

export function exactModuleAtelierPayload(value: unknown, modelId: string, moduleId: string): ModuleAtelierPayload {
  if (!record(value) || !exactKeys(value, ['ok', 'modelId', 'instanceId', 'atelier', 'resolutionContext', 'bundleVersion', 'bundleHash'])
    || value.ok !== true || value.modelId !== modelId || value.instanceId !== moduleId
    || !Number.isSafeInteger(value.bundleVersion) || Number(value.bundleVersion) < 0
    || !(value.bundleHash === null || value.bundleHash === undefined || nonempty(value.bundleHash))) {
    throw new Error('MODULE_ATELIER_RESPONSE_INVALID');
  }
  const state = value.atelier;
  if (!record(state) || !exactKeys(state, ['schema', 'intent', 'projections', 'issues', 'gates'])
    || state.schema !== 'holzcom.module_atelier_intent.v1' || !(state.intent === null || record(state.intent))
    || !Array.isArray(state.projections) || !Array.isArray(state.issues) || !record(state.gates)
    || !exactKeys(state.gates, ['blockSave', 'blockRelease'])
    || typeof state.gates.blockSave !== 'boolean' || typeof state.gates.blockRelease !== 'boolean') {
    throw new Error('MODULE_ATELIER_RESPONSE_INVALID');
  }
  if (record(state.intent)) {
    const intentKeys = ['schema', 'moduleKind', 'editingMode', 'openingDefaults', 'sections', 'facadeSets', 'drawerSets'];
    const hasResolution = record(state.intent.resolution);
    const hasConstruction = record(state.intent.construction);
    if (!exactKeys(state.intent, [
      ...intentKeys,
      ...(hasConstruction ? ['construction'] : []),
      ...(hasResolution ? ['resolution'] : []),
    ])
      || state.intent.schema !== 'holzcom.module_atelier_intent.v1'
      || !nonempty(state.intent.moduleKind) || !['automatic', 'manual'].includes(String(state.intent.editingMode))
      || !record(state.intent.openingDefaults) || !Array.isArray(state.intent.sections)
      || !Array.isArray(state.intent.facadeSets) || !Array.isArray(state.intent.drawerSets)
      || (state.intent.resolution !== undefined && !hasResolution)
      || (state.intent.construction !== undefined && !hasConstruction)) throw new Error('MODULE_ATELIER_RESPONSE_INVALID');
  }
  if (!state.projections.every((projection) => record(projection)
    && exactKeys(projection, ['sectionId', 'slotId', 'nicheEntityId', 'zones'])
    && nonempty(projection.sectionId) && nonempty(projection.slotId) && nonempty(projection.nicheEntityId)
    && Array.isArray(projection.zones) && projection.zones.every((zone) => record(zone)
      && exactKeys(zone, ['id', 'slotId', 'nicheEntityId'])
      && nonempty(zone.id) && nonempty(zone.slotId) && nonempty(zone.nicheEntityId)))) {
    throw new Error('MODULE_ATELIER_RESPONSE_INVALID');
  }
  const context = value.resolutionContext;
  const contextKeys = ['rulesVersion', 'catalogVersion', 'materialsVersion', 'hardwareVersion', 'hostRevision'] as const;
  if (!record(context) || !exactKeys(context, contextKeys) || !contextKeys.every((key) => nonempty(context[key]))) {
    throw new Error('ATELIER_RESOLUTION_CONTEXT_UNAVAILABLE');
  }
  if (!state.issues.every((issue) => record(issue) && exactKeys(issue, ['code', 'gate', 'subject', 'limit'])
    && nonempty(issue.code) && nonempty(issue.gate) && record(issue.subject) && record(issue.limit)
    && exactKeys(issue.limit, ['source', 'scope']) && nonempty(issue.limit.source) && nonempty(issue.limit.scope))) {
    throw new Error('MODULE_ATELIER_RESPONSE_INVALID');
  }
  return value as unknown as ModuleAtelierPayload;
}
